<template>
  <!-- A single resizable rail (like EditorSideList) split into labelled sections,
       each with its own add button and within-section drag-to-reorder. Used as the
       army editor's second rail (mirrors the documents > sections double rail).
       Shared rail chrome lives in styles/rail.css; this rail adds the army button,
       section headers, and add-section action. -->
  <aside class="st-rail seg-list st-shell" :style="{ width: width + 'px' }">
    <StButton
      class="seg-list__army"
      :class="{ active: armySelected }"
      variant="ghost"
      icon="shield-halved"
      on-shell
      block
      @click="emit('selectArmy')"
    >
      {{ armyLabel || '(unnamed army)' }}
    </StButton>

    <template v-for="section in sections" :key="section.key">
      <div class="seg-list__section">
        <div class="seg-list__header">
          <!-- A selectable header acts as a node (e.g. an enhancement category you
             can rename/delete); a plain header is just a label. -->
          <StButton
            v-if="section.selectable"
            class="seg-list__heading seg-list__heading--button"
            :class="{ active: selectedSectionKey === section.key }"
            variant="ghost"
            size="sm"
            on-shell
            @click="emit('selectSection', section.key)"
          >
            {{ section.label }}
          </StButton>
          <span v-else class="seg-list__heading">{{ section.label }}</span>
          <StIconButton
            class="seg-list__add"
            icon="plus"
            :label="'Add ' + section.label"
            size="sm"
            on-shell
            @click="emit('add', section.key)"
          />
        </div>

        <ul v-if="section.items.length" class="st-rail__items">
          <li
            v-for="(item, index) in section.items"
            :key="index"
            class="st-rail__item"
            :class="{
              active: selectedKey === section.key && selectedIndex === index,
              dragging: drag.key === section.key && drag.index === index,
              'drop-target':
                over.key === section.key && over.index === index && drag.index !== index,
            }"
            draggable="true"
            @click="emit('select', section.key, index)"
            @dragstart="onDragStart(section.key, index, $event)"
            @dragover.prevent="onDragOver(section.key, index)"
            @drop="onDrop(section.key, index)"
            @dragend="onDragEnd"
          >
            <span
              class="st-rail__handle"
              title="Drag to reorder"
              @mousedown="dragArmed = true"
              @click.stop
            >
              <FontAwesomeIcon icon="grip-vertical" />
            </span>
            <span class="st-rail__label">{{ item.name || '(unnamed)' }}</span>
          </li>
        </ul>
        <p v-else class="st-rail__empty">{{ section.emptyHint ?? 'None yet.' }}</p>
      </div>

      <!-- Add-section action, rendered right after a chosen section (e.g. add a
           new enhancement category, placed above the lores so it stays reachable
           even with many units below). -->
      <StButton
        v-if="addSectionLabel && section.key === addSectionAfterKey"
        class="seg-list__add-section"
        variant="ghost"
        size="sm"
        icon="plus"
        on-shell
        @click="emit('addSection')"
      >
        {{ addSectionLabel }}
      </StButton>
    </template>

    <!-- Drag the right edge to resize (width persisted per widthKey). -->
    <div
      class="st-rail__resize"
      :class="{ resizing }"
      title="Drag to resize"
      @mousedown="startResize"
    />
  </aside>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { StButton, StIconButton } from './ui/index.js';
import { useRailResize } from '../composables/useRailResize.js';
import { useDragReorder } from '../composables/useDragReorder.js';

export interface SegmentSection {
  key: string;
  label: string;
  items: { name: string }[];
  emptyHint?: string;
  /** When true the header is a button that selects the section itself (a node). */
  selectable?: boolean;
}

const props = defineProps<{
  sections: SegmentSection[];
  armyLabel: string;
  /** True when the army panel (not a section item) is selected. */
  armySelected: boolean;
  /** Selected section key (which section the selected item belongs to). */
  selectedKey: string | null;
  selectedIndex: number;
  /** Selected section header (for selectable sections). */
  selectedSectionKey?: string | null;
  /** When set, renders a button (labelled this) that emits `addSection`. */
  addSectionLabel?: string;
  /** The section key to render the add-section button after. */
  addSectionAfterKey?: string;
  widthKey: string;
}>();

const emit = defineEmits<{
  selectArmy: [];
  select: [key: string, index: number];
  selectSection: [key: string];
  add: [key: string];
  addSection: [];
  reorder: [key: string, from: number, to: number];
}>();

const { width, resizing, startResize } = useRailResize(props.widthKey, { default: 220 });

const { dragArmed, drag, over, onDragStart, onDragOver, onDrop, onDragEnd } =
  useDragReorder<string>((key, from, to) => emit('reorder', key, from, to));
</script>

<style scoped>
.seg-list {
  gap: var(--st-space-3);
  overflow-y: auto;
}

.seg-list__army {
  border-radius: var(--st-radius-xs);
  font-size: var(--st-text-md);
  justify-content: flex-start;
  min-width: 0;
  overflow: hidden;
}
.seg-list__army :deep(.st-btn__icon) {
  flex-shrink: 0;
}
.seg-list__army :deep(.st-btn__label) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.seg-list__army.active,
.seg-list__heading--button.active {
  background: var(--st-shell-surface-active);
  color: var(--st-shell-text);
  font-weight: var(--st-weight-semibold);
}

.seg-list__section {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.seg-list__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--st-space-2);
}
.seg-list__heading {
  font-size: var(--st-text-xs);
  font-weight: var(--st-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--st-shell-text-subtle);
}
.seg-list__heading--button {
  height: auto;
  min-height: var(--st-control-h-sm);
  padding: 0 var(--st-space-1);
  justify-content: flex-start;
  text-transform: uppercase;
  letter-spacing: var(--st-tracking-wide);
}
.seg-list__add-section {
  border: 1px dashed var(--st-shell-border);
  border-radius: var(--st-radius-xs);
  justify-content: flex-start;
}
.seg-list__add {
  border-radius: var(--st-radius-xs);
}
</style>
